import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import {
  AcknowledgementConfig,
  ConfigType,
  FormFieldConfig
} from './../../../form-template-model';
import { FormTemplateGeneratorService } from './../../form-template-generator.service';

@Component({
  selector: 'csi-form-template-acknowledgement-field-generator',
  templateUrl: './form-template-acknowledgement-field-generator.component.html',
  styleUrls: ['./form-template-acknowledgement-field-generator.component.css']
})
export class FormTemplateAcknowledgementFieldGeneratorComponent implements OnInit {
  @Input() config: FormFieldConfig & { typeConfig: AcknowledgementConfig };
  @Input() formTemplateFieldKey: string;
  @Input() formTemplateGroupFormGroup: FormGroup;
  @Input() readOnly: boolean;
  @Input() formatForPDF: boolean;
  @Input() isConsentForm: boolean = false;
  @Input() supportingFields: { [key: string]: FormFieldConfig };

  /** `'minimal'` = quiet separators + focus/invalid accent (default). */
  readonly consentAckChrome: 'minimal' | 'boxed' = 'minimal';

  contentFields: Array<{ key: string; value: FormFieldConfig }> = [];
  initialsField: { key: string; value: FormFieldConfig } | null = null;
  sideBySide = false;
  focused = false;

  constructor(public formTemplateGeneratorService: FormTemplateGeneratorService) {}

  ngOnInit() {
    const nestedFields = this.config && this.config.typeConfig && this.config.typeConfig.formFields;
    if (!nestedFields) {
      return;
    }

    const sortedFields = Object.keys(nestedFields)
      .map(key => ({ key, value: nestedFields[key] }))
      .sort((a, b) => (a.value.sort || 0) - (b.value.sort || 0));

    const initialsIndex = sortedFields.findIndex(
      field =>
        field.value &&
        field.value.typeConfig &&
        field.value.typeConfig.type === ConfigType.initials
    );

    if (initialsIndex >= 0) {
      this.initialsField = sortedFields[initialsIndex];
      this.contentFields = sortedFields.filter((_, index) => index !== initialsIndex);
    } else {
      this.contentFields = sortedFields;
      this.initialsField = null;
    }

    this.sideBySide = !!(this.initialsField && this.contentFields.length > 0);
  }

  onInitialsFocusIn() {
    this.focused = true;
  }

  onInitialsFocusOut(event: FocusEvent) {
    const currentTarget = event.currentTarget as HTMLElement;
    const relatedTarget = event.relatedTarget as Node;
    if (currentTarget && relatedTarget && currentTarget.contains(relatedTarget)) {
      return;
    }
    this.focused = false;
  }

  get isInvalid(): boolean {
    if (!this.formTemplateGroupFormGroup) {
      return false;
    }

    if (this.initialsField) {
      const initialsControl = this.formTemplateGroupFormGroup.controls[this.initialsField.key];
      if (this.isControlInvalid(initialsControl)) {
        return true;
      }
    }

    for (let index = 0; index < this.contentFields.length; index++) {
      const contentField = this.contentFields[index];
      const control = this.formTemplateGroupFormGroup.controls[contentField.key];
      if (this.isControlInvalid(control)) {
        return true;
      }
    }

    return false;
  }

  private isControlInvalid(control: AbstractControl | null | undefined): boolean {
    if (!control) {
      return false;
    }
    if (control.invalid && (control.touched || control.dirty)) {
      return true;
    }
    if (control instanceof FormGroup) {
      return Object.keys(control.controls).some(key =>
        this.isControlInvalid(control.controls[key])
      );
    }
    return false;
  }

  get hideForPdf(): boolean {
    return !!(
      this.formatForPDF &&
      this.initialsField &&
      this.formTemplateGeneratorService.fieldsHiddenForPDFExportMap[this.initialsField.key]
    );
  }
}
